import * as Clipboard from "expo-clipboard";
// ====== React imports ======
import { useMemo, useState } from "react";
// ====== External Libraries ======
import { IconButton, Menu } from "react-native-paper";

import { getFeedbackReasonsByType } from "@memorang/helpers/feedback";
import Box from "@memorang/ui/components/Box";
import { FeedbackDialog } from "@memorang/ui/components/FeedbackDialog";
import { useToast } from "@memorang/ui/components/Toast";
import TooltipWrapper from "@memorang/ui/components/TooltipWrapper";
import type { IconType } from "@memorang/ui/icon";
import { useDevice } from "@memorang/ui/useDevice";
import { router } from "expo-router";
// ====== Local components ======
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import { TTSContainer } from "../TTS";
import type { FeedbackParams } from "../feedback";
import { useSendFeedback } from "../feedback/hooks/useSendFeedback";
import type { HandleTracking } from "../types";
import { useAppTheme } from "../useAppTheme";
import { events, feedbackActionIcons, summaryActionIcons } from "./constants";
import { useSendEmail } from "./hooks/useSendEmail";

// const emailInstructionMessage = (email?: string) => {
// 	const isEduEmail = email?.endsWith(".edu");
// 	return `If you do not receive an email, check your junk, spam, and recently deleted folders${isEduEmail ? ", or contact your IT administrator" : ""}`;
// };
type Props = {
	text?: string;
	searchQuery?: string;
	showEmail?: boolean;
	streaming?: boolean;
	handleTracking: HandleTracking;
	feedbackDialogParams?: FeedbackParams;
	onFeedbackDialogOpen?: () => void;
	align?:
		| "flex-start"
		| "flex-end"
		| "center"
		| "space-between"
		| "space-around"
		| "space-evenly"
		| undefined;
};

const SummaryActions = ({
	text,
	handleTracking,
	feedbackDialogParams,
	align = "flex-start",
	searchQuery,
	showEmail,
	streaming,
	onFeedbackDialogOpen,
}: Props) => {
	const sendFeedback = useSendFeedback();
	const sendEmail = useSendEmail();
	const { t } = useTranslation();
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const [isEmailSent, setIsEmailSent] = useState(false);
	const [inProgress, setInProgress] = useState<Record<string, boolean>>({});
	const [isMenuVisible, setIsMenuVisible] = useState(false);
	const [feedbackType, setFeedbackType] = useState<
		"positive" | "negative" | "reportIssue"
	>();
	const { showToast } = useToast();
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const handleDismissFeedback = () => {
		setShowFeedbackDialog(false);
	};

	const handleSendEmail = async () => {
		await sendEmail({
			text,
			examName: feedbackDialogParams?.context,
			toEmail: feedbackDialogParams?.email,
			companyName: feedbackDialogParams?.companyName,
		});
		handleTracking(events.emailSent);
	};

	const handleNavigateToFeedback = (
		feedbackRatingType: "positive" | "negative" | "reportIssue",
	) => {
		const isReportIssue = feedbackRatingType === "reportIssue";
		const isNegative = feedbackRatingType === "negative";
		const isPositive = feedbackRatingType === "positive";

		const getFeedbackTitle = () => {
			if (isReportIssue) {
				return t("dialog.report.title");
			}
			return undefined;
		};

		const getFeedbackSubtitle = () => {
			if (isReportIssue) {
				return t("dialog.report.body");
			}
			if (isNegative) {
				return t("dialog.feedback.negativeBody");
			}
			if (isPositive) {
				return t("dialog.feedback.positiveBody");
			}
			return t("report.sessionAnalysis.feedbackDialog.body");
		};

		const getFeedbackIcon = () => {
			if (isReportIssue) {
				return "flag-outline";
			}
			return undefined;
		};

		const getFeedbackCta = () => {
			if (isReportIssue) {
				return t("dialog.report.cta");
			}
			return undefined;
		};

		router.push({
			pathname: "/feedback",
			params: {
				view: feedbackDialogParams?.view ?? "session",
				featureName: feedbackDialogParams?.feature,
				feedbackTitle: getFeedbackTitle(),
				feedbackSubtitle: getFeedbackSubtitle(),
				feedbackIcon: getFeedbackIcon(),
				itemFeedbackRating:
					feedbackRatingType === "reportIssue"
						? "negative"
						: feedbackRatingType,
				sessionId: feedbackDialogParams?.sessionId,
				type:
					feedbackRatingType === "reportIssue" ? "Content Flagged" : undefined,
				placeholder: t("report.sessionAnalysis.feedbackDialog.placeholder"),
				feedbackCta: getFeedbackCta(),
				feedbackReasonType:
					feedbackRatingType === "negative"
						? "negative"
						: feedbackRatingType === "reportIssue"
							? "report"
							: undefined,
			},
		});
	};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const handleActionIconPress = async (type: string) => {
		switch (type) {
			case "copy": {
				const textToCopy = searchQuery
					? `# Question:\n${searchQuery}\n\n# Answer:\n${text}`
					: text!;
				await Clipboard.setStringAsync(textToCopy);
				handleTracking(events.sessionReportSummaryCopyClicked, {
					query: searchQuery!,
					summary: text!,
				});
				showToast({
					message: t("report.sessionAnalysis.menuActions.copiedAnalysis"),
					duration: 3000,
				});
				break;
			}
			case "feedback-positive":
			case "feedback-negative": {
				const feedbackRatingType =
					type === "feedback-positive" ? "positive" : "negative";
				handleTracking(
					type === "feedback-positive"
						? events.aiFeedbackPositive
						: events.aiFeedbackNegative,
				);
				if (isMobile) {
					handleNavigateToFeedback(feedbackRatingType);
				} else {
					onFeedbackDialogOpen?.();
					setFeedbackType(feedbackRatingType);
					setShowFeedbackDialog(true);
				}
				break;
			}
			case "report-issue": {
				handleTracking(events.aiFeedbackReport);
				if (isMobile) {
					handleNavigateToFeedback("reportIssue");
				} else {
					onFeedbackDialogOpen?.();
					setFeedbackType("reportIssue");
					setShowFeedbackDialog(true);
				}
				break;
			}
			case "email": {
				try {
					setInProgress({ ...inProgress, email: true });
					handleTracking(events.emailTriggered);
					await handleSendEmail();
					setIsEmailSent(true);
					showToast({
						message: t("report.sessionAnalysis.menuActions.emailSent", {
							email: feedbackDialogParams?.email,
							emailInstructionMessage: t(
								"report.sessionAnalysis.menuActions.emailInstructionMessage",
							),
						}),
						duration: 3000,
					});
				} catch (error) {
					showToast({
						message: t("report.sessionAnalysis.menuActions.emailFailed", {
							email: feedbackDialogParams?.email,
						}),
						duration: 3000,
					});
					console.error(error);
				} finally {
					setInProgress({ ...inProgress, email: false });
				}
				break;
			}
			default:
				break;
		}
	};

	const handleOpenMenu = () => {
		setIsMenuVisible(true);
	};

	const handleCloseMenu = () => {
		setIsMenuVisible(false);
	};

	const actionItems = useMemo(() => {
		if (Platform.OS === "web") {
			return [
				...summaryActionIcons(showEmail, isEmailSent),
				...feedbackActionIcons,
			];
		}
		return summaryActionIcons(showEmail, isEmailSent);
	}, [showEmail, isEmailSent]);

	const handlePostFeedback = (feedback: string, email: string) => {
		handleTracking(events.feedbackSubmitted, {
			type: feedbackType!,
			feedback,
			email,
		});
	};

	const isReportIssue = feedbackType === "reportIssue";
	const isNegative = feedbackType === "negative";
	const isPositive = feedbackType === "positive";

	const negativeReasonList = useMemo(
		() => getFeedbackReasonsByType<IconType>("negative", t),
		[t],
	);

	const reportReasonList = useMemo(
		() => getFeedbackReasonsByType<IconType>("report", t),
		[t],
	);

	const feedbackReasonList = isReportIssue
		? reportReasonList
		: isNegative
			? negativeReasonList
			: undefined;

	const getSubtitle = () => {
		if (isReportIssue) {
			return t("dialog.report.body");
		}
		if (isNegative) {
			return t("dialog.feedback.negativeBody");
		}
		if (isPositive) {
			return t("dialog.feedback.positiveBody");
		}
		return t("report.sessionAnalysis.feedbackDialog.body");
	};

	return (
		<Box flexDirection="row" justifyContent={align} alignItems="center">
			{actionItems.map(({ icon, type, tooltip, disabled }) => (
				<TooltipWrapper
					key={icon}
					title={t(tooltip) || ""}
					enterTouchDelay={500}
				>
					<IconButton
						icon={icon}
						size={18}
						iconColor={theme.colors.onPrimaryContainer}
						loading={inProgress[type]}
						onPress={() => handleActionIconPress(type)}
						style={{
							margin: 0,
						}}
						disabled={disabled || inProgress[type] || streaming}
					/>
				</TooltipWrapper>
			))}
			<TTSContainer
				explanation={text}
				size={16}
				itemId={`${feedbackDialogParams?.sessionId}`}
				mode={undefined}
				iconColor={theme.colors.onPrimaryContainer}
			/>
			{Platform.OS !== "web" && (
				<Menu
					visible={isMenuVisible}
					onDismiss={handleCloseMenu}
					anchorPosition="bottom"
					anchor={
						<IconButton
							icon="dots-vertical"
							size={18}
							iconColor={theme.colors.onPrimaryContainer}
							onPress={handleOpenMenu}
							style={{
								margin: 0,
							}}
						/>
					}
				>
					{feedbackActionIcons.map(({ icon, type, tooltip }) => (
						<Menu.Item
							key={icon}
							leadingIcon={icon}
							title={t(tooltip)}
							onPress={() => {
								handleActionIconPress(type);
								handleCloseMenu();
							}}
						/>
					))}
				</Menu>
			)}
			{showFeedbackDialog && feedbackDialogParams && (
				<FeedbackDialog
					key={`feedback-${feedbackType ?? "default"}`}
					visible={showFeedbackDialog}
					handlePostFeedback={handlePostFeedback}
					handleTracking={handleTracking}
					onDismiss={handleDismissFeedback}
					sendFeedback={sendFeedback}
					feedbackDialogParams={{
						...feedbackDialogParams,
						type:
							feedbackType === "reportIssue"
								? "Content Flagged"
								: feedbackDialogParams.type,
					}}
					feedbackPlaceholder={t(
						"report.sessionAnalysis.feedbackDialog.placeholder",
					)}
					title={isReportIssue ? t("dialog.report.title") : undefined}
					subtitle={getSubtitle()}
					icon={isReportIssue ? "flag-outline" : undefined}
					cta={isReportIssue ? t("dialog.report.cta") : undefined}
					feedbackReasonList={feedbackReasonList}
				/>
			)}
		</Box>
	);
};

export { SummaryActions };
